import * as CallReducer from 'store/call/call.reducer'
import * as ChatReducer from 'store/chat/chat.reducer'
import { IChat } from 'store/chat/chat.state'
import * as ContactReducer from 'store/contact/contact.reducer'
import * as ContactsReducer from 'store/contacts/contacts.reducer'
import * as TasksReducer from 'store/tasks/tasks.reducer'
import { ITask } from 'store/tasks/tasks.state'

import { createSlice, PayloadAction } from '@reduxjs/toolkit'

import ACWState, { ACWContactType } from './acw.state'

export interface SetACWDurationAction {
    contactID: string
    durationSeconds: number
}

const initialState: ACWState = {}

const startACW = (state: ACWState, contactID: string, type: ACWContactType): ACWState => {
    if (!contactID) return state

    return { ...state, [contactID]: { contactID, type, startedAt: Date.now() } }
}

const extendACW = (state: ACWState, contactID: string): ACWState => {
    const acw = state[contactID]
    if (!acw) return state

    return { ...state, [contactID]: { ...acw, extendedAt: Date.now() } }
}

const endACW = (state: ACWState, contactID: string): ACWState => {
    if (!state[contactID]) return state

    const { [contactID]: completed, ...rest } = state
    return rest
}

const recoverACW = (
    state: ACWState,
    connections: Array<IChat | ITask>,
    type: ACWContactType,
): ACWState =>
    connections.reduce<ACWState>(
        (acc, connection) =>
            connection.acw && connection.acwStartedAt
                ? {
                      ...acc,
                      [connection.id]: {
                          contactID: connection.id,
                          type,
                          startedAt: connection.acwStartedAt,
                          extendedAt: connection.acwExtendedAt,
                      },
                  }
                : acc,
        state,
    )

export const acwSlice = createSlice({
    name: 'acw',
    initialState,
    reducers: {
        setACWDuration(state, action: PayloadAction<SetACWDurationAction>) {
            const acw = state[action.payload.contactID]
            if (!acw) return state

            return {
                ...state,
                [action.payload.contactID]: {
                    ...acw,
                    durationSeconds: action.payload.durationSeconds,
                },
            }
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(ContactReducer.callACW, (state, action) =>
                startACW(state, action.payload, 'voice'),
            )
            .addCase(ContactReducer.extendCallACW, (state, action) =>
                extendACW(state, action.payload),
            )
            .addCase(ContactReducer.completeCallACW, (state, action) =>
                endACW(state, action.payload),
            )
            .addCase(CallReducer.completeCallACW, (state) =>
                Object.values(state).reduce(
                    (acc, acw) => (acw.type === 'voice' ? endACW(acc, acw.contactID) : acc),
                    state,
                ),
            )
            .addCase(ChatReducer.chatACW, (state, action) =>
                startACW(state, action.payload, 'chat'),
            )
            .addCase(ChatReducer.extendChatACW, (state, action) =>
                extendACW(state, action.payload),
            )
            .addCase(ChatReducer.completeChatACW, (state, action) => endACW(state, action.payload))
            .addCase(ChatReducer.removeChatConnection, (state, action) =>
                endACW(state, action.payload),
            )
            .addCase(ChatReducer.recoverChats, (state, action) =>
                recoverACW(state, action.payload, 'chat'),
            )
            .addCase(TasksReducer.taskACW, (state, action) =>
                startACW(state, action.payload, 'task'),
            )
            .addCase(TasksReducer.extendTaskACW, (state, action) =>
                extendACW(state, action.payload),
            )
            .addCase(TasksReducer.completeTaskACW, (state, action) => endACW(state, action.payload))
            .addCase(TasksReducer.removeTaskConnection, (state, action) =>
                endACW(state, action.payload),
            )
            .addCase(TasksReducer.recoverTasks, (state, action) =>
                recoverACW(state, action.payload, 'task'),
            )
            .addCase(ContactsReducer.filterContacts, (state, action) =>
                endACW(state, action.payload),
            )
    },
})

export const { setACWDuration } = acwSlice.actions

export type ACWAction = ReturnType<(typeof acwSlice.actions)[keyof typeof acwSlice.actions]>

export default acwSlice.reducer
